import React, { FC, useEffect } from 'react'
import {
  Text,
  Box,
  VStack,
  Image,
  HStack,
  LinkBox,
  LinkOverlay,
  Heading,
  Badge,
  Flex,
  SimpleGrid,
  Spinner,
  Link
} from '@chakra-ui/react'
import { Link as RouterLink } from 'react-router-dom'
import moment from 'moment'
import { DashboardLayout } from '../../components'
import useDashboardStore from '../../state/Dashboard.store'
import { money, titleCase } from '../../utils/formatter'
import { IPolicy } from '../../types'
import { ROUTES } from '../../contants'

const Policy = () => {
  const [loading, policies, getPolicies] = useDashboardStore((state) => [
    state.loading,
    state.policies,
    state.getPolicies
  ])

  useEffect(() => {
    getPolicies()
  }, [getPolicies])

  return (
    <DashboardLayout title="Policies">
      {loading === 'loading' ? (
        <VStack h="100%" justify="center" flex={1}>
          <Spinner size="xl" />
        </VStack>
      ) : loading === 'success' || loading === 'updating' ? (
        policies?.length ? (
          policies?.map((policy: IPolicy) => (
            <BuyBox
              key={policy.id}
              id={policy.id}
              amount={policy.amount}
              status={policy.policy_status}
              time={policy.product.period}
              expiry={policy.expiration_date}
              src={`/images/buy-${policy.policy_type === 'vehicle' ? 'car' : 'gadget'}.png`}
              title={`${policy.policy_type === 'gadget' ? 'Gadget' : 'Motor'} Insurance`}
              description={
                policy[policy.policy_type].make
                  ? `${policy[policy.policy_type].make} ${policy[policy.policy_type].model}`
                  : `${policy[policy.policy_type].model}`
              }
            />
          ))
        ) : (
          <Box
            mt="1rem"
            mb="2rem"
            borderRadius="1.2rem"
            overflow="hidden"
            textAlign="center"
            border="1px dashed #FF1B1B">
            <Image mb="3.2rem" mt="4.2rem" mx="auto" maxW="95px" src="/images/policy.png" />

            <Text color="black">You have no Insurance Protection!</Text>
            <HStack
              mt="2.4rem"
              justify="center"
              bg="rgba(255, 27, 27,  0.05)"
              py="1.3rem"
              px="3rem">
              <Link
                to={ROUTES.insurance}
                as={RouterLink}
                color="brand.blue"
                display="flex"
                justifyContent="center"
                alignItems="center"
                fontWeight="600"
                fontSize="1.2rem"
                _hover={{ border: 'none', outline: 'none' }}
                _active={{ border: 'none', outline: 'none' }}
                _focus={{ border: 'none', outline: 'none' }}>
                Buy an Insurance Plan
                <Image ml="0.5rem" src="/icons/arrow.svg" />
              </Link>
            </HStack>
          </Box>
        )
      ) : null}
    </DashboardLayout>
  )
}

export { Policy }

type Props = {
  title: string
  description: string
  src: string
  time: string
  amount: number
  id: string
  status: string
  expiry: any
}

const BuyBox: FC<Props> = ({ title, description, src, time, id, amount, status, expiry }) => {
  status = status.toLowerCase()

  const active = moment.duration(moment(expiry).diff(moment()), 'milliseconds').asDays() > 0

  return (
    <LinkBox
      as={VStack}
      mb="1.6rem"
      pos="relative"
      border="1px solid #F7F7F7"
      borderRadius="1.6rem"
      overflow="hidden"
      boxShadow="0px 4px 12px rgba(154, 160, 166, 0.08)">
      <Box overflow="hidden" h="100px">
        <Image objectFit="cover" w="100%" src={src} />
      </Box>
      <Box w="full" pt="1.5rem !important" p="2rem">
        <HStack justify="space-between">
          <VStack align="flex-start">
            <Heading color="black" fontWeight="500" fontSize="1.2rem">
              {title}
            </Heading>
            <LinkOverlay fontSize="1.1rem" as={RouterLink} to={`/policies/${id}`}>
              {description}
            </LinkOverlay>
          </VStack>
          <VStack align="flex-start" color="brand.blue">
            <Heading fontSize="1.6rem">{money(amount)}</Heading>
            <Text fontSize="1rem">per {time}</Text>
          </VStack>
        </HStack>
        <Flex mt="1rem" w="full" justify="flex-end">
          <Badge
            ml="auto"
            fontWeight="400"
            textTransform="capitalize"
            px="1.4rem"
            py="0.2rem"
            fontSize="1.1rem"
            bg={
              active
                ? 'rgba(10, 184, 111, 0.1)'
                : status === 'pending'
                ? 'rgba(255, 27, 27, 0.1)'
                : 'rgba(253, 128, 57, 0.1)'
            }
            color={
              active ? '#0AB86F' : status === 'pending' ? 'rgba(253, 128, 57, 0.99)' : '#FF1B1B'
            }
            display="flex"
            alignItems="center"
            borderRadius="1.2rem"
            colorScheme="green">
            {status === 'approved' ? (
              active ? (
                <>
                  <svg
                    width="10"
                    style={{ marginRight: '5px' }}
                    height="10"
                    viewBox="0 0 10 10"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M4.99914 7.5L2.06014 9.295L2.85914 5.945L0.244141 3.705L3.67664 3.43L4.99914 0.25L6.32164 3.43L9.75464 3.705L7.13914 5.945L7.93814 9.295L4.99914 7.5Z"
                      fill="#0AB86F"
                    />
                  </svg>
                  Active
                </>
              ) : (
                'Expired'
              )
            ) : (
              titleCase(status)
            )}
          </Badge>
        </Flex>
      </Box>
    </LinkBox>
  )
}
